import type { TextOptions, Widget, WidgetStyle, WidgetType } from './types';
import { ASPECTS } from './types';
import { MAKRUH_DEFAULTS, type MakruhProps } from './makruh';

export type WidgetCategory = 'prayer' | 'media' | 'info' | 'giving';

export interface WidgetDef<P = Record<string, unknown>> {
  type: WidgetType;
  label: string;
  category: WidgetCategory;
  /** Icon name from the app's icon set. */
  icon: string;
  /** Default size in cells on a 12 × 8 landscape grid. */
  size: { w: number; h: number };
  /** Smallest size the content stays readable at. */
  min: { w: number; h: number };
  defaults: () => P;
  /** Base text size (pt at 1080p) for the main line of this widget. */
  textSize: number;
}

/* ---------- widget props ---------- */
/** Colours for one prayer's tile; empty values fall back to the widget's tile colours. */
export type TileColours = { tile?: string | null; text?: string | null };
export type PrayerTimesProps = {
  design?: string; title: string; showSunrise: boolean; showJamaah: boolean; showNext: boolean; layout: 'auto' | 'row' | 'column'; h12: boolean;
  /** Icons design: draw a rounded card around each prayer. */
  cards?: boolean;
  /** Add Tarawih on days the timetable has a Tarawih time (Ramadan). On unless switched off. */
  showTarawih?: boolean;
  /** Colour of every prayer's tile, and of the text on it. Empty uses the widget's style. */
  tileColor?: string | null; tileText?: string | null;
  /** Per-prayer overrides (fajr, sunrise, dhuhr, asr, maghrib, isha). */
  prayerColors?: Partial<Record<'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha', TileColours>>;
  /** Board design: the font of the times (e.g. a digital "Seven Segment" face), and their colour. Empty: the widget's. */
  digitFont?: string | null;
  digitColor?: string | null;
  /** Board design: each salah's Arabic name beside its English name. On unless switched off. */
  showArabic?: boolean;
};
/** The font and colour of a widget's numbers (the time, a countdown, the prayer times), e.g. green "Seven Segment". */
export type DigitLook = { digitFont?: string | null; digitColor?: string | null };
/**
 * "Digital LED": seven-segment digits, like a station or masjid board, on the Clock, Prayer Times and Countdown. The
 * layout is the widget's own (digital clock, board of times, digital countdown); only the numbers change. Their
 * colour can be picked (red by default), and a style's own digit font and colour still win.
 */
export const LED_FONT = 'Seven Segment';
export const LED_COLOURS = ['#FF3B30', '#39FF6A', '#FFB020', '#FFFFFF', '#38BDF8', '#FF2D95'] as const;
export const LED_DEFAULT = LED_COLOURS[0];
export const isLed = (w: Pick<Widget, 'type' | 'props'>) => (w.type === 'clock' || w.type === 'prayerTimes' || w.type === 'countdown') && designOf(w) === 'led';
export type ClockProps = { design?: string; showSeconds: boolean; showDate: boolean; showHijri: boolean; h12: boolean } & DigitLook;
export type JumuahProps = { design?: string; title: string; mode: 'countdown' | 'times' };
export type CountdownProps = { design?: string; target: 'nextJamaah' | 'nextStart' | 'custom'; label: string; customAt?: string; customLabel?: string } & DigitLook;
/**
 * Custom text: a Heading (one line or two) and a Paragraph (with an optional title). The limits keep any amount of
 * text readable inside the widget at its smallest size; the Studio stops typing there and screens never show more.
 */
export const HEADING_MAX = 60, PARAGRAPH_MAX = 400, PARAGRAPH_TITLE_MAX = 40;
export type HeadingProps = { design?: string; text: string };
export type ParagraphProps = { design?: string; title: string; text: string };
export type Announcement = { id: string; title: string; body: string };
export type AnnouncementsProps = {
  design?: string; title: string; items: Announcement[]; seconds: number;
  /** "Urgent message" design: one message styled for its kind (Janazah, car, sisters, emergency, notice). */
  alert?: { preset: import('./broadcast').MessagePreset; heading: string; body: string };
};
export type Poster = { id: string; src: string; alt?: string };
/** Real poster shapes. A3, A4 and A5 share one shape, so one setting covers all three. */
export type PosterShape = 'a-portrait' | 'a-landscape' | 'phone' | 'square' | 'widescreen' | 'any';
export const POSTER_SHAPES: Array<{ value: PosterShape; label: string; detail: string; ratio: number | null }> = [
  { value: 'a-portrait', label: 'A4 portrait', detail: 'A3, A4 and A5 posters', ratio: 1 / Math.SQRT2 },
  { value: 'a-landscape', label: 'A4 landscape', detail: 'A3, A4 and A5 on their side', ratio: Math.SQRT2 },
  { value: 'phone', label: 'Phone screenshot', detail: 'Tall, like a phone screen', ratio: 9 / 19.5 },
  { value: 'square', label: 'Square', detail: 'Social media posts', ratio: 1 },
  { value: 'widescreen', label: 'Widescreen', detail: '16:9 slides and photos', ratio: 16 / 9 },
  { value: 'any', label: 'Any size', detail: 'Fill whatever space you give it', ratio: null },
];
export const posterRatio = (shape: unknown) => POSTER_SHAPES.find((s) => s.value === shape)?.ratio ?? null;
/** The poster shape closest to an image's width ÷ height. */
export const nearestPosterShape = (ratio: number): PosterShape =>
  POSTER_SHAPES.filter((s) => s.ratio).reduce((a, b) => (Math.abs(Math.log(b.ratio! / ratio)) < Math.abs(Math.log(a.ratio! / ratio)) ? b : a)).value;
export type PostersProps = {
  design?: string; items: Poster[]; seconds: number; fit: 'cover' | 'contain'; transition: 'fade' | 'slide'; shape: PosterShape;
  /** "Slide in and out": the edge of the screen each poster comes in from (and goes back out to). */
  enter?: 'left' | 'right' | 'top' | 'bottom';
  /** "Slide in and out": seconds with no poster between one going and the next arriving. */
  gap?: number;
  /** "Slide in and out": how quickly each poster slides or fades in and out. */
  speed?: SlideSpeed;
};
export type SlideSpeed = 'vslow' | 'slow' | 'medium' | 'fast' | 'vfast';
export const SLIDE_SPEEDS: Array<{ value: SlideSpeed; label: string; seconds: number }> = [
  { value: 'vslow', label: 'Very slow', seconds: 5 }, { value: 'slow', label: 'Slow', seconds: 3 }, { value: 'medium', label: 'Medium', seconds: 1.8 },
  { value: 'fast', label: 'Fast', seconds: 1.1 }, { value: 'vfast', label: 'Very fast', seconds: 0.6 },
];
export type LogoProps = { fit: 'contain' | 'cover' };
/** Ramadan widgets (Iftar, Sehri ends, Tarawih): times from the timetable's Maghrib, Sehri Ends and Tarawih columns. */
export type RamadanProps = { design?: string; title: string; showCountdown: boolean; showDay: boolean; h12: boolean };
/** An appeal: its name, the amount raised and the target. The percentage, what's left and donors are each optional. */
export type DonationsProps = {
  design?: string; appeal: string; raised: number; goal: number; currency: string;
  showPercent?: boolean; showRemaining?: boolean; showDonors?: boolean; donors?: number;
  /** Older widgets: no longer shown. */
  title?: string; note?: string;
};
export type DonationQrProps = { design?: string; title: string; url: string; caption: string };
export type ScriptureProps = {
  design?: string; title: string; mode: 'daily' | 'pick' | 'custom'; text: string; reference: string; arabic?: string;
  /** New each day from: a hadith collection, or a theme of ayat ('all' or empty for everything). */
  source?: string;
  /** The one chosen (mode 'pick'). */
  pick?: string;
  /** Ayah: show the Arabic above the meaning. Hadith: show who narrated it. On unless switched off. */
  showArabic?: boolean; showNarrator?: boolean;
};
export type EventItem = { id: string; title: string; date: string; time?: string; place?: string };
export type EventsProps = { design?: string; title: string; items: EventItem[]; max: number };
export type TickerProps = { design?: string; tag: string; items: Array<{ id: string; text: string }>; speed: number };

/**
 * Each widget can be drawn in several designs (an analog clock, a flip clock, prayer times with icons…). The first
 * design in each list is the default. Colours, fonts and styles apply to every design.
 */
const SCRIPTURE_DESIGNS = [
  { value: 'centered', label: 'Centred' }, { value: 'quote', label: 'Quote' }, { value: 'minimal', label: 'Text only' },
  { value: 'card', label: 'Card' }, { value: 'split', label: 'Side by side' }, { value: 'highlight', label: 'Highlight' },
];
const RAMADAN_DESIGNS = [
  { value: 'time', label: 'Big time' }, { value: 'countdown', label: 'Countdown' }, { value: 'ring', label: 'Ring' },
  { value: 'bar', label: 'Progress bar' }, { value: 'flip', label: 'Flip' }, { value: 'arc', label: 'Sky arc' },
];
export const WIDGET_DESIGNS: Partial<Record<WidgetType, Array<{ value: string; label: string }>>> = {
  clock: [
    { value: 'digital', label: 'Digital' }, { value: 'analog', label: 'Analog' }, { value: 'analog-minimal', label: 'Minimal analog' },
    { value: 'analog-arabic', label: 'Arabic numerals' }, { value: 'flip', label: 'Flip clock' }, { value: 'stacked', label: 'Stacked' },
    { value: 'led', label: 'Digital LED' },
  ],
  prayerTimes: [
    { value: 'tiles', label: 'Tiles' }, { value: 'icons', label: 'Icons' }, { value: 'clocks', label: 'Analog clocks' },
    { value: 'table', label: 'Table' }, { value: 'focus', label: 'Next prayer' }, { value: 'timeline', label: 'Timeline' },
    { value: 'board', label: 'Board' }, { value: 'led', label: 'Digital LED' },
  ],
  countdown: [{ value: 'digital', label: 'Digital' }, { value: 'ring', label: 'Ring' }, { value: 'hourglass', label: 'Hourglass' }, { value: 'flip', label: 'Flip' }, { value: 'bar', label: 'Progress bar' }, { value: 'words', label: 'In words' }, { value: 'led', label: 'Digital LED' }],
  donations: [{ value: 'bar', label: 'Progress bar' }, { value: 'segments', label: 'Loading bar' }, { value: 'cards', label: 'Cards' }, { value: 'ring', label: 'Ring' }, { value: 'numbers', label: 'Numbers only' }, { value: 'gauge', label: 'Gauge' }],
  jumuah: [{ value: 'countdown', label: 'Countdown' }, { value: 'times', label: 'All times' }, { value: 'focus', label: 'Big times' }, { value: 'ring', label: 'Ring' }, { value: 'flip', label: 'Flip' }, { value: 'bar', label: 'Week bar' }],
  events: [{ value: 'list', label: 'List' }, { value: 'cards', label: 'Cards' }, { value: 'timeline', label: 'Timeline' }, { value: 'agenda', label: 'Agenda' }, { value: 'spotlight', label: 'Spotlight' }, { value: 'days', label: 'Days to go' }],
  ticker: [{ value: 'tag', label: 'With tag' }, { value: 'plain', label: 'Plain' }, { value: 'pills', label: 'Pills' }, { value: 'fade', label: 'One at a time' }, { value: 'headline', label: 'Headline band' }, { value: 'clock', label: 'With clock' }],
  announcements: [{ value: 'slides', label: 'Slides' }, { value: 'list', label: 'List' }, { value: 'grid', label: 'Grid' }, { value: 'featured', label: 'Featured' }, { value: 'headlines', label: 'Headlines' }, { value: 'alert', label: 'Urgent message' }],
  ayah: SCRIPTURE_DESIGNS,
  hadith: SCRIPTURE_DESIGNS,
  donationQr: [{ value: 'card', label: 'Card' }, { value: 'side', label: 'Side by side' }, { value: 'minimal', label: 'Code only' }, { value: 'scanner', label: 'Scanner' }, { value: 'poster', label: 'Poster' }, { value: 'accent', label: 'Colour card' }],
  posters: [
    { value: 'single', label: 'One at a time' }, { value: 'framed', label: 'Framed' }, { value: 'zoom', label: 'Slow zoom' },
    { value: 'progress', label: 'Progress bars' }, { value: 'stack', label: 'Stack' }, { value: 'counter', label: 'With counter' }, { value: 'flyin', label: 'Slide in and out' },
  ],
  logo: [
    { value: 'plain', label: 'Logo only' }, { value: 'name', label: 'With name' }, { value: 'stacked', label: 'Stacked' },
    { value: 'badge', label: 'Badge' }, { value: 'plate', label: 'White card' }, { value: 'wordmark', label: 'Name only' },
  ],
  iftar: RAMADAN_DESIGNS,
  sehri: RAMADAN_DESIGNS,
  tarawih: RAMADAN_DESIGNS,
  heading: [
    { value: 'plain', label: 'Plain' }, { value: 'underline', label: 'Underline' }, { value: 'bar', label: 'Accent bar' }, { value: 'banner', label: 'Banner' },
    { value: 'outline', label: 'Outline' }, { value: 'colour', label: 'Accent text' },
  ],
  paragraph: [
    { value: 'plain', label: 'Plain' }, { value: 'accent', label: 'Accent line' }, { value: 'card', label: 'Card' }, { value: 'quote', label: 'Quote' },
    { value: 'outline', label: 'Outline' }, { value: 'header', label: 'Title strip' },
  ],
};
/** The design a widget uses (its own, or the default for its type). */
export function designOf(w: Pick<Widget, 'type' | 'props'>): string {
  const list = WIDGET_DESIGNS[w.type];
  if (!list) return 'default';
  const d = (w.props as { design?: unknown }).design;
  if (w.type === 'jumuah' && !d) return (w.props as { mode?: string }).mode === 'times' ? 'times' : 'countdown';
  return list.some((x) => x.value === d) ? (d as string) : list[0]!.value;
}

/** Widgets whose content can be scaled with the Content size slider (pictures always fill their widget). */
export const canScaleContent = (type: WidgetType, design?: string) => type !== 'posters' && (type !== 'logo' || design === 'name' || design === 'stacked' || design === 'wordmark');

/**
 * Settings a design doesn't use, so the Studio hides them (e.g. 12-hour for an analog face, the tag for a plain
 * ticker). Everything not listed here works in every design; the display tests check that each one has an effect.
 */
const UNUSED: Record<string, string[]> = {
  'clock:analog': ['h12'], 'clock:analog-minimal': ['h12'], 'clock:analog-arabic': ['h12'],
  'prayerTimes:tiles': ['cards'], 'prayerTimes:table': ['layout', 'cards'], 'prayerTimes:focus': ['layout', 'cards', 'title', 'showNext', 'tiles'],
  'prayerTimes:icons': ['layout'], 'prayerTimes:clocks': ['layout'], 'prayerTimes:board': ['layout', 'cards'], 'prayerTimes:led': ['layout', 'cards'],
  'ticker:plain': ['tag'], 'ticker:pills': ['tag'], 'ticker:headline': ['tag'], 'ticker:clock': ['tag'],
  'prayerTimes:timeline': ['layout', 'cards'], 'announcements:featured': ['seconds'], 'announcements:headlines': ['seconds'],
  'announcements:list': ['seconds'], 'announcements:grid': ['seconds'], 'announcements:alert': ['seconds', 'title', 'accent'],
  'ayah:minimal': ['title', 'showArabic'], 'hadith:minimal': ['title'],
  'donationQr:minimal': ['title', 'caption'],
  'heading:plain': ['accent'], 'paragraph:plain': ['accent'], 'paragraph:card': ['accent'],
  ...Object.fromEntries(['iftar', 'sehri', 'tarawih'].flatMap((t) => ['countdown', 'ring', 'bar', 'flip', 'arc'].map((d) => [`${t}:${d}`, ['showCountdown']]))),
};
export const designUses = (w: Pick<Widget, 'type' | 'props'>, prop: string) => !(UNUSED[`${w.type}:${designOf(w)}`] ?? []).includes(prop);

let seq = 0;
export const newId = (prefix = 'w') => `${prefix}_${Date.now().toString(36)}${(seq = (seq + 1) % 1296).toString(36).padStart(2, '0')}${Math.random().toString(36).slice(2, 6)}`;

export const WIDGETS: Record<WidgetType, WidgetDef> = {
  prayerTimes: {
    type: 'prayerTimes', label: 'Prayer Times', category: 'prayer', icon: 'prayer', size: { w: 8, h: 4 }, min: { w: 3, h: 2 }, textSize: 56,
    defaults: (): PrayerTimesProps => ({ title: 'Prayer Times', showSunrise: true, showJamaah: true, showNext: true, layout: 'auto', h12: false }),
  },
  clock: {
    type: 'clock', label: 'Clock', category: 'prayer', icon: 'clock', size: { w: 4, h: 4 }, min: { w: 2, h: 1 }, textSize: 100,
    defaults: (): ClockProps => ({ showSeconds: false, showDate: true, showHijri: true, h12: false }),
  },
  jumuah: {
    type: 'jumuah', label: 'Jumu‘ah', category: 'prayer', icon: 'calendar', size: { w: 4, h: 3 }, min: { w: 2, h: 2 }, textSize: 76,
    defaults: (): JumuahProps => ({ title: 'Jumu‘ah', mode: 'countdown' }),
  },
  countdown: {
    type: 'countdown', label: 'Countdown', category: 'prayer', icon: 'hourglass', size: { w: 4, h: 3 }, min: { w: 2, h: 1 }, textSize: 96,
    defaults: (): CountdownProps => ({ target: 'nextJamaah', label: 'Jama‘ah in' }),
  },
  announcements: {
    type: 'announcements', label: 'Announcements', category: 'info', icon: 'megaphone', size: { w: 6, h: 3 }, min: { w: 3, h: 2 }, textSize: 44,
    defaults: (): AnnouncementsProps => ({ title: 'Announcements', seconds: 10, items: [
      { id: newId('a'), title: 'Weekly halaqah', body: 'Every Thursday after Maghrib in the main hall. All welcome.' },
      { id: newId('a'), title: 'Sisters’ class', body: 'Resumes Monday at 11am in the upstairs room.' },
    ] }),
  },
  posters: {
    type: 'posters', label: 'Posters', category: 'media', icon: 'image', size: { w: 3, h: 6 }, min: { w: 1, h: 2 }, textSize: 32,
    defaults: (): PostersProps => ({ items: [], seconds: 12, fit: 'contain', transition: 'fade', shape: 'a-portrait', enter: 'right', gap: 15 }),
  },
  donations: {
    type: 'donations', label: 'Donations', category: 'giving', icon: 'heart', size: { w: 6, h: 3 }, min: { w: 4, h: 2 }, textSize: 80,
    defaults: (): DonationsProps => ({ appeal: 'Masjid extension', raised: 18450, goal: 50000, currency: '£', showPercent: true, showRemaining: true, showDonors: false, donors: 120 }),
  },
  donationQr: {
    type: 'donationQr', label: 'Donation QR', category: 'giving', icon: 'qr', size: { w: 3, h: 3 }, min: { w: 2, h: 2 }, textSize: 34,
    defaults: (): DonationQrProps => ({ title: 'Donate', url: 'https://example.org/donate', caption: 'Scan to give' }),
  },
  ayah: {
    type: 'ayah', label: 'Ayah of the Day', category: 'info', icon: 'book', size: { w: 4, h: 3 }, min: { w: 3, h: 2 }, textSize: 42,
    defaults: (): ScriptureProps => ({ title: 'Ayah of the Day', mode: 'daily', text: '', reference: '' }),
  },
  hadith: {
    type: 'hadith', label: 'Hadith', category: 'info', icon: 'quote', size: { w: 4, h: 3 }, min: { w: 3, h: 2 }, textSize: 40,
    defaults: (): ScriptureProps => ({ title: 'Hadith', mode: 'daily', text: '', reference: '' }),
  },
  events: {
    type: 'events', label: 'Events', category: 'info', icon: 'events', size: { w: 4, h: 4 }, min: { w: 3, h: 2 }, textSize: 36,
    defaults: (): EventsProps => {
      // Two examples a few days ahead, so a new widget shows how it looks. Editors replace them with their own.
      const inDays = (n: number) => new Date(Date.now() + n * 86400_000).toISOString().slice(0, 10);
      return { title: 'Upcoming', max: 4, items: [
        { id: newId('e'), title: 'Family halaqah', date: inDays(3), time: '19:30', place: 'Main hall' },
        { id: newId('e'), title: 'Youth football', date: inDays(6), time: '10:00', place: 'Park' },
      ] };
    },
  },
  ticker: {
    type: 'ticker', label: 'News Ticker', category: 'info', icon: 'ticker', size: { w: 12, h: 1 }, min: { w: 4, h: 1 }, textSize: 36,
    defaults: (): TickerProps => ({ tag: 'NEWS', speed: 60, items: [
      { id: newId('t'), text: 'Weekly halaqah after Maghrib on Thursdays' },
      { id: newId('t'), text: 'Car park closed Saturday 9–11am' },
      { id: newId('t'), text: 'Sisters’ class resumes Monday' },
    ] }),
  },
  logo: {
    type: 'logo', label: 'Masjid Logo', category: 'media', icon: 'logo', size: { w: 2, h: 2 }, min: { w: 1, h: 1 }, textSize: 32,
    defaults: (): LogoProps => ({ fit: 'contain' }),
  },
  iftar: {
    type: 'iftar', label: 'Iftar', category: 'prayer', icon: 'sunset', size: { w: 4, h: 3 }, min: { w: 2, h: 2 }, textSize: 80,
    defaults: (): RamadanProps => ({ title: 'Iftar', showCountdown: true, showDay: true, h12: false }),
  },
  sehri: {
    type: 'sehri', label: 'Sehri Ends', category: 'prayer', icon: 'moonStar', size: { w: 4, h: 3 }, min: { w: 2, h: 2 }, textSize: 80,
    defaults: (): RamadanProps => ({ title: 'Sehri ends', showCountdown: true, showDay: true, h12: false }),
  },
  tarawih: {
    type: 'tarawih', label: 'Tarawih', category: 'prayer', icon: 'lamp', size: { w: 4, h: 3 }, min: { w: 2, h: 2 }, textSize: 80,
    defaults: (): RamadanProps => ({ title: 'Tarawih', showCountdown: true, showDay: true, h12: false }),
  },
  // Always a full-width strip along the bottom, two rows high (24 × 2 landscape, 12 × 2 portrait): see sizeFor.
  makruh: {
    type: 'makruh', label: 'Makruh Times', category: 'prayer', icon: 'makruh', size: { w: 12, h: 1 }, min: { w: 6, h: 1 }, textSize: 48,
    defaults: (): MakruhProps => ({ ...MAKRUH_DEFAULTS }),
  },
  // Smallest sizes: a full 60-character heading and a full 400-character paragraph still read from across a hall.
  heading: {
    type: 'heading', label: 'Heading', category: 'info', icon: 'heading', size: { w: 6, h: 2 }, min: { w: 3, h: 1 }, textSize: 72,
    defaults: (): HeadingProps => ({ text: 'Welcome to our masjid' }),
  },
  paragraph: {
    type: 'paragraph', label: 'Paragraph', category: 'info', icon: 'paragraph', size: { w: 5, h: 3 }, min: { w: 4, h: 3 }, textSize: 36,
    defaults: (): ParagraphProps => ({ title: '', text: 'Please switch your phone to silent before entering the prayer hall. JazakAllahu khayran.' }),
  },
};

export const WIDGET_ORDER: WidgetType[] = ['prayerTimes', 'clock', 'jumuah', 'countdown', 'makruh', 'announcements', 'heading', 'paragraph', 'posters', 'donations', 'donationQr', 'ayah', 'hadith', 'events', 'ticker', 'logo', 'iftar', 'sehri', 'tarawih'];

export const CATEGORY_LABEL: Record<WidgetCategory, string> = { prayer: 'Prayer', media: 'Media', info: 'Info', giving: 'Giving' };

export const ACCENTS = ['#0A84FF', '#30D158', '#FFD60A', '#FF9F0A', '#FF453A', '#BF5AF2', '#FFFFFF'] as const;
/**
 * The colours offered for a screen's background (a solid colour, or a gradient's glow and base colour): the core
 * colours people reach for first. Any other colour is one tap away in the colour picker.
 */
export const BACKGROUND_SWATCHES = ['#000000', '#FFFFFF', '#8E8E93', '#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#00A6A6', '#007AFF', '#0A2A66', '#AF52DE', '#FF2D55'] as const;
export const STYLES: Array<{ value: WidgetStyle; label: string }> = [
  { value: 'glass', label: 'Glass' }, { value: '3d', label: '3D Drop' }, { value: 'flat', label: 'Flat' }, { value: 'legacy', label: 'Legacy' }, { value: 'none', label: 'None' },
];
/** Text sizes offered in the Studio (pt at 1080p). The +/− buttons step by 1 between them. */
export const TEXT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 80, 90, 100] as const;
export const MIN_TEXT = 10, MAX_TEXT = 100;
export type FontGroup = 'Clean' | 'Rounded' | 'Condensed' | 'Digital' | 'Elegant' | 'Arabic';
/** Every font is stored with the app, so screens show it even without an internet connection. */
export const FONT_LIST: Array<{ name: string; group: FontGroup; stack: string; note?: string }> = [
  { name: 'SF Pro Display', group: 'Clean', stack: '"SF Pro Display",-apple-system,BlinkMacSystemFont,"Inter Variable","Segoe UI",Roboto,sans-serif', note: 'Apple’s system font (Inter on Windows)' },
  { name: 'Inter', group: 'Clean', stack: '"Inter Variable","Inter",sans-serif' },
  { name: 'DM Sans', group: 'Clean', stack: '"DM Sans Variable","Inter Variable",sans-serif' },
  { name: 'Manrope', group: 'Clean', stack: '"Manrope Variable","Inter Variable",sans-serif' },
  { name: 'Outfit', group: 'Clean', stack: '"Outfit Variable","Inter Variable",sans-serif' },
  { name: 'Sora', group: 'Clean', stack: '"Sora Variable","Inter Variable",sans-serif' },
  { name: 'Urbanist', group: 'Clean', stack: '"Urbanist Variable","Inter Variable",sans-serif' },
  { name: 'Plus Jakarta Sans', group: 'Clean', stack: '"Plus Jakarta Sans Variable","Inter Variable",sans-serif' },
  { name: 'Montserrat', group: 'Clean', stack: '"Montserrat Variable","Inter Variable",sans-serif' },
  { name: 'Lexend', group: 'Clean', stack: '"Lexend Variable","Inter Variable",sans-serif' },
  { name: 'Space Grotesk', group: 'Clean', stack: '"Space Grotesk Variable","Inter Variable",sans-serif' },
  { name: 'Nunito', group: 'Rounded', stack: '"Nunito Variable","Inter Variable",sans-serif' },
  { name: 'Quicksand', group: 'Rounded', stack: '"Quicksand Variable","Inter Variable",sans-serif' },
  { name: 'Barlow Condensed', group: 'Condensed', stack: '"Barlow Condensed","Inter Variable",sans-serif' },
  { name: 'Oswald', group: 'Condensed', stack: '"Oswald Variable","Barlow Condensed",sans-serif' },
  { name: 'Bebas Neue', group: 'Condensed', stack: '"Bebas Neue","Oswald Variable",sans-serif', note: 'Capitals only' },
  { name: 'Orbitron', group: 'Digital', stack: '"Orbitron Variable","Inter Variable",sans-serif' },
  { name: 'Chakra Petch', group: 'Digital', stack: '"Chakra Petch","Inter Variable",sans-serif' },
  { name: 'Share Tech Mono', group: 'Digital', stack: '"Share Tech Mono",ui-monospace,monospace' },
  { name: 'Seven Segment', group: 'Digital', stack: '"DSEG7 Classic","Share Tech Mono",monospace', note: 'Like a digital clock; best for numbers' },
  { name: 'Playfair Display', group: 'Elegant', stack: '"Playfair Display Variable",Georgia,serif' },
  { name: 'Noto Naskh Arabic', group: 'Arabic', stack: '"Noto Naskh Arabic Variable","Amiri","Inter Variable",serif' },
  { name: 'Amiri', group: 'Arabic', stack: '"Amiri","Noto Naskh Arabic Variable",serif' },
  { name: 'Reem Kufi', group: 'Arabic', stack: '"Reem Kufi Variable","Noto Naskh Arabic Variable",sans-serif' },
];
export const FONTS = FONT_LIST.map((f) => f.name);
export const fontStack = (name: string) => FONT_LIST.find((f) => f.name === name)?.stack ?? FONT_LIST[0]!.stack;

export function defaultText(type: WidgetType): TextOptions {
  return { font: 'SF Pro Display', weight: type === 'paragraph' ? 500 : type === 'heading' ? 700 : 600, size: WIDGETS[type].textSize, align: type === 'ticker' || type === 'paragraph' ? 'left' : 'center', case: 'normal', scale: true };
}

/** A new widget of `type` with sensible defaults, scaled to the target grid. */
export function createWidget(type: WidgetType, at: { x: number; y: number; w?: number; h?: number }): Widget {
  const def = WIDGETS[type];
  return {
    id: newId(), type, x: at.x, y: at.y, w: at.w ?? def.size.w, h: at.h ?? def.size.h,
    style: 'glass', accent: '#0A84FF', opacity: type === 'logo' ? 0 : 100, text: defaultText(type), props: def.defaults() as Record<string, unknown>,
  };
}

/**
 * Default and minimum size on a given grid. Sizes are defined on a 12 × 8 landscape grid and converted by real cell
 * size, so a widget is about the same physical size on the TV whatever the screen shape.
 */
export function sizeFor(type: WidgetType, cols: number, rows: number): { w: number; h: number; minW: number; minH: number } {
  const def = WIDGETS[type];
  // Makruh Times: the full width of the screen, two rows high, and never less than half the width or two rows.
  if (type === 'makruh') return { w: cols, h: Math.min(rows, 2), minW: Math.ceil(cols / 2), minH: Math.min(rows, 2) };
  const a = Object.values(ASPECTS).find((x) => x.cols === cols && x.rows === rows) ?? { width: 1920, height: 1080 };
  const sx = (1920 / 12) / (a.width / cols), sy = (1080 / 8) / (a.height / rows);
  const w = Math.max(1, Math.min(cols, Math.round(def.size.w * sx) || 1));
  const h = Math.max(1, Math.min(rows, Math.round(def.size.h * sy) || 1));
  const minW = Math.max(1, Math.min(cols, Math.floor(def.min.w * sx + 0.01) || 1));
  const minH = Math.max(1, Math.min(rows, Math.floor(def.min.h * sy + 0.01) || 1));
  return { w: Math.max(w, minW), h: Math.max(h, minH), minW, minH };
}

export const widgetName = (w: Widget) => w.name?.trim() || WIDGETS[w.type].label;

/** What people typed or uploaded into a widget. Reset keeps these; everything else goes back to the defaults. */
const CONTENT_KEYS = ['items', 'title', 'url', 'caption', 'appeal', 'raised', 'goal', 'currency', 'donors', 'note', 'text', 'reference', 'arabic', 'mode', 'tag', 'label', 'target', 'customAt', 'customLabel', 'max', 'seconds', 'speed', 'shape'];

/**
 * Reset widget: removes its own colours, border, text options, content size, design and settings, keeping its
 * place, size and content. Its look comes back to match the rest of the screen (the style, colours and font most
 * other widgets use), or NoorView's default look on an otherwise empty screen.
 */
export function resetWidget(w: Widget, others: readonly Widget[]): Widget {
  const fresh = createWidget(w.type, { x: w.x, y: w.y, w: w.w, h: w.h });
  const peers = others.filter((o) => o.id !== w.id && o.type !== 'logo' && o.type !== 'posters');
  const most = <T,>(f: (o: Widget) => T): T | undefined => {
    const n = new Map<string, { v: T; c: number }>();
    for (const o of peers) { const v = f(o); const k = JSON.stringify(v ?? null); const e = n.get(k); if (e) e.c++; else n.set(k, { v, c: 1 }); }
    return [...n.values()].sort((a, b) => b.c - a.c)[0]?.v;
  };
  const out: Widget = {
    ...fresh, id: w.id, name: w.name, hidden: w.hidden, locked: w.locked,
    props: { ...fresh.props, ...Object.fromEntries(CONTENT_KEYS.filter((k) => k in w.props).map((k) => [k, w.props[k]])) },
  };
  if (!out.name) delete out.name;
  if (out.hidden === undefined) delete out.hidden;
  if (out.locked === undefined) delete out.locked;
  if (peers.length && w.type !== 'logo') {
    out.style = most((o) => o.style) ?? out.style;
    out.accent = most((o) => o.accent) ?? out.accent;
    out.opacity = most((o) => o.opacity) ?? out.opacity;
    const fill = most((o) => o.fill ?? null), border = most((o) => o.border ?? null), radius = most((o) => o.radius ?? null);
    if (radius != null) out.radius = radius;
    if (fill) out.fill = fill;
    if (border) out.border = border;
    out.text = { ...out.text, font: most((o) => o.text.font) ?? out.text.font };
  }
  return out;
}
